import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Palette,
  ArrowUpRight,
  Check,
  Users,
  Clock,
  Globe,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Shield,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Palette, title: 'Branded sidebar', text: 'Upload your logo and set your workspace name so agents see your brand every day.' },
  { icon: Globe, title: 'Custom help center', text: 'Point help.yourcompany.com to a fully branded knowledge base.' },
  { icon: Sparkles, title: 'Branded widget', text: 'Match the live chat widget colors to your brand and remove vendor branding.' },
  { icon: Users, title: 'One consistent experience', text: 'Customers stay inside your visual identity from chat to tickets to docs.' },
];

const benefits = [
  { icon: Users, title: '+35% brand trust', description: 'Customers trust brands they recognize over third-party tools.' },
  { icon: Clock, title: 'Minutes to setup', description: 'Upload your logo, set colors, and connect your domain in minutes.' },
  { icon: Globe, title: 'Custom domain', description: 'Host your help center on your own subdomain with free SSL.' },
  { icon: Shield, title: 'Enterprise control', description: 'Toggle white label per workspace with owner-only permissions.' },
];

const useCases = [
  { title: 'Agencies', text: 'Run Zuri Hub under each client brand without spinning up new infrastructure.' },
  { title: 'SaaS support', text: 'Keep users inside your product experience while using Zuri Hub on the back end.' },
  { title: 'Multi-brand retail', text: 'Separate brand identities per workspace while sharing the same team.' },
  { title: 'Resellers', text: 'Offer branded support tools to your customers under your own identity.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Which plan includes white label?',
    answer: 'White Label is included in the Enterprise plan.',
  },
  {
    question: 'Can I use my own domain?',
    answer: 'Yes. You can point a custom subdomain to your help center and widget.',
  },
  {
    question: 'Does it change the mobile experience?',
    answer: 'Yes. The widget and portal will match your branding on every device.',
  },
  {
    question: 'Can I have different brands per workspace?',
    answer: 'Yes. Enterprise customers can enable different branding for each workspace.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function WhiteLabelPage() {
  return (
    <>
      <SEO
        title="White Label - Branded Customer Support | ZuriHub"
        description="Rebrand Zuri Hub with your logo, colors, and domain for a fully owned customer experience. Enterprise white-label solution for agencies and SaaS."
        path="/white-label"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Palette className="h-4 w-4" /> Enterprise Only
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Make Zuri Hub look and feel like <span className="gradient-text">your product</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Replace the Zuri Hub sidebar, widgets, and help center with your own logo, colors, and custom domain. Customers never know we are there.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Palette className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/AB_White.png"
                  alt="ZuriHub White Label showing branded workspace and custom domain"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful white-label features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need for a fully branded experience.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose white label</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform brand perception.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Upload your logo, set brand colors, connect your domain, and enable across all channels. Your brand, everywhere.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Palette,
                    title: 'Upload your logo',
                    description: 'Add your logo, favicon, and workspace name from white-label settings.',
                  },
                  {
                    icon: Sparkles,
                    title: 'Set brand colors',
                    description: 'Pick the primary color that matches your brand guidelines.',
                  },
                  {
                    icon: Globe,
                    title: 'Connect your domain',
                    description: 'Point a custom subdomain to your help center and widget.',
                  },
                  {
                    icon: Shield,
                    title: 'Enable across channels',
                    description: 'The same branding applies to sidebar, portal, and live chat.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">White-label features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Branded sidebar</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Custom domain</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Widget styling</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Portal branding</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Email branding</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Enterprise control</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Launch a branded support experience</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Talk to our Enterprise team to enable White Label for your workspace.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about white label</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
